import { Box, Chip, Input, Stack, Typography } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useAuthUser } from 'auth/hooks';
import { AttributeType } from 'companySettings/enums';
import { Button, Error, PageTitle } from 'core/components';
import Breadcrumbs from 'core/components/Breadcrumbs';
import { ExpandableCard } from 'core/components/ExpandableCard';
import { useGetPartFinderCatalogFacets, useGetPartFinderFamilyDefinition } from 'partFinder/hooks';
import { partFinderCatalogPath, partFinderFamilyPartsPath } from 'partFinder/routes/routePaths';
import {
  IPartFinderFamilyDefinition,
  IPartFinderFamilySearchAttribute,
  IPartFinderFamilyValue,
  IPartFinderSelection,
} from 'partFinder/types';
import {
  EMPTY_SELECTION,
  getAttributeUnits,
  getErrorStatusCode,
  selectionFromSearch,
  selectionToSearch,
  usePartFinderReturn,
} from 'partFinder/utils';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { AppliedFilters, IAppliedFilter } from '../AppliedFilters';

interface IProps {
  familyId: number;
  // Opened from the catalog's Source column: no page chrome, closes itself on Show Results.
  isDrawer?: boolean;
  // Where the family's name and part count go instead of above the filters, e.g. the drawer's
  // sticky header.
  headingContainer?: HTMLElement | null;
  initialSelection?: IPartFinderSelection;
  onClose?: () => void;
}

const getRangeHint = (values: IPartFinderFamilyValue[]) => {
  const mins = values.map(({ rangeMin }) => rangeMin).filter((min): min is number => min !== null);
  const maxes = values
    .map(({ rangeMax, rangeMin }) => rangeMax ?? rangeMin)
    .filter((max): max is number => max !== null);

  return mins.length ? `${Math.min(...mins)} – ${Math.max(...maxes)}` : '';
};

// A value keeps its box whether it can be picked or not, an unused one only paler. Joy paints the
// box on the chip's inner action layer (near-white for a disabled chip), so that layer takes
// the colour of the chip itself.
const getValueChipSx = (colorHex: string | null, isPicked: boolean): SxProps => {
  if (isPicked) {
    return { borderRadius: '6px' };
  }

  return {
    borderRadius: '6px',
    backgroundColor: colorHex ? `${colorHex}33` : 'neutral.200',
    '& .MuiChip-action': { backgroundColor: 'inherit' },
    '&:hover .MuiChip-action': { backgroundColor: colorHex ? `${colorHex}4D` : 'neutral.300' },
    '&.Mui-disabled': {
      backgroundColor: colorHex ? `${colorHex}14` : 'neutral.100',
      color: 'neutral.400',
      opacity: 1,
      '& .MuiChip-action': { backgroundColor: 'inherit' },
    },
  };
};

const omitKey = <T,>(record: Record<number, T>, key: number): Record<number, T> =>
  Object.fromEntries(Object.entries(record).filter(([id]) => Number(id) !== key));

// A typed number waits this long before it narrows the counts: each change is one count query
// per attribute on the server.
const NUMBER_DEBOUNCE_MS = 400;

export const PartFinderFamilySearch = ({
  familyId,
  isDrawer = false,
  headingContainer,
  initialSelection,
  onClose,
}: IProps) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const { returnTo, navigateWithReturn, cancel } = usePartFinderReturn();

  // The catalog's location and stock narrowing arrives in the URL.
  const [selection, setSelection] = useState<IPartFinderSelection>(
    () => initialSelection ?? selectionFromSearch(search)
  );

  // What the user typed in a number box, shown at once; the selection takes it after a pause.
  const [numberDrafts, setNumberDrafts] = useState<Record<number, string>>({});

  const { user } = useAuthUser();
  const {
    data: loadedDefinition,
    isError,
    error,
  } = useGetPartFinderFamilyDefinition(familyId, selection);

  // The last definition that loaded, kept across a failed request: react-query hands the previous
  // data back only while the new request has not failed, so the page would otherwise lose the
  // family and its filters to the error on the first failed count.
  const [data, setData] = useState<IPartFinderFamilyDefinition>();

  useEffect(() => {
    if (loadedDefinition) {
      setData(loadedDefinition);
    }
  }, [loadedDefinition]);

  // The error replaces the page only when there is nothing loaded to show.
  const showError = isError && !data;
  const { data: facets } = useGetPartFinderCatalogFacets();

  const toggleValue = (attributeId: number, value: string) =>
    setSelection((current) => {
      const picked = current.values[attributeId] ?? [];
      const next = picked.includes(value)
        ? picked.filter((item) => item !== value)
        : [...picked, value];
      const rest = omitKey(current.values, attributeId);

      return { ...current, values: next.length ? { ...rest, [attributeId]: next } : rest };
    });

  const setNumber = (attributeId: number, number: string) => {
    setNumberDrafts((current) => omitKey(current, attributeId));
    setSelection((current) => {
      const rest = omitKey(current.numbers, attributeId);

      return { ...current, numbers: number === '' ? rest : { ...rest, [attributeId]: number } };
    });
  };

  const setNumberLater = useMemo(() => debounce(setNumber, NUMBER_DEBOUNCE_MS), []);

  useEffect(() => () => setNumberLater.cancel(), [setNumberLater]);

  const onNumberChange = (attributeId: number, number: string) => {
    setNumberDrafts((current) => ({ ...current, [attributeId]: number }));
    setNumberLater(attributeId, number);
  };

  const removeLocation = (locationId: number) =>
    setSelection((current) => ({
      ...current,
      locationIds: current.locationIds.filter((id) => id !== locationId),
    }));

  const removeManufacturer = (manufacturerId: number) =>
    setSelection((current) => ({
      ...current,
      manufacturerIds: current.manufacturerIds.filter((id) => id !== manufacturerId),
    }));

  const myLocationId = user?.location?.id ? Number(user.location.id) : null;

  // The chips above the cards: everything narrowing the counts, the catalog's stock, location and
  // manufacturer choice included, so nothing narrows them that cannot be seen and lifted here.
  const appliedFilters = useMemo<IAppliedFilter[]>(
    () => [
      ...(selection.inStock
        ? [
            {
              key: 'inStock',
              label: t('labelInStock'),
              onRemove: () => setSelection((current) => ({ ...current, inStock: false })),
            },
          ]
        : []),
      ...selection.locationIds.map((locationId) => ({
        key: `location${locationId}`,
        label:
          locationId === myLocationId
            ? t('labelMyLocation')
            : facets?.locations?.find(({ id }) => id === locationId)?.name ?? String(locationId),
        onRemove: () => removeLocation(locationId),
      })),
      ...selection.manufacturerIds.map((manufacturerId) => ({
        key: `manufacturer${manufacturerId}`,
        label:
          facets?.manufacturers?.find(({ id }) => id === manufacturerId)?.name ??
          String(manufacturerId),
        onRemove: () => removeManufacturer(manufacturerId),
      })),
      ...Object.entries(selection.values).flatMap(([attributeId, picked]) =>
        picked.map((value) => ({
          key: `${attributeId}:${value}`,
          label: value,
          onRemove: () => toggleValue(Number(attributeId), value),
        }))
      ),
      ...Object.entries(selection.numbers).map(([attributeId, number]) => ({
        key: `number${attributeId}`,
        label: `${
          data?.attributes.find(({ id }) => id === Number(attributeId))?.name ?? ''
        }: ${number}`,
        onRemove: () => setNumber(Number(attributeId), ''),
      })),
    ],
    // toggleValue, setNumber, removeLocation and removeManufacturer only use the state setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selection, data, facets, myLocationId, t]
  );

  const pickedCount = appliedFilters.length;

  // Undefined until the family is loaded; a plural label has nothing to count before that.
  const partsCount = pickedCount ? data?.matchedCount : data?.family.partsCount;

  const resetAll = () => {
    setNumberLater.cancel();
    setNumberDrafts({});
    setSelection({ ...EMPTY_SELECTION });
  };

  const renderValues = (attribute: IPartFinderFamilySearchAttribute) => (
    <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 1 }}>
      {attribute.values.map((item) => {
        const value = item.value ?? '';
        const isPicked = (selection.values[attribute.id] ?? []).includes(value);
        const isUnavailable = !isPicked && item.partsCount === 0;

        return (
          <Chip
            key={value}
            size={Size.LG}
            variant={isPicked ? Variant.SOLID : Variant.SOFT}
            color={isPicked ? Color.PRIMARY : Color.NEUTRAL}
            disabled={isUnavailable}
            onClick={() => toggleValue(attribute.id, value)}
            sx={getValueChipSx(item.colorHex, isPicked)}
          >
            {value}
          </Chip>
        );
      })}
    </Stack>
  );

  const renderNumber = (attribute: IPartFinderFamilySearchAttribute) => (
    <Input
      size={Size.SM}
      type="number"
      sx={{ maxWidth: 320 }}
      placeholder={t('placeholderPartFinderNumber', { range: getRangeHint(attribute.values) })}
      value={numberDrafts[attribute.id] ?? selection.numbers[attribute.id] ?? ''}
      onChange={(event) => onNumberChange(attribute.id, event.target.value)}
    />
  );

  const renderAttribute = (attribute: IPartFinderFamilySearchAttribute) => (
    <ExpandableCard
      key={attribute.id}
      title={attribute.name}
      variant={Variant.OUTLINED}
      defaultExpanded
    >
      {attribute.type === AttributeType.NUMBER_RANGE
        ? renderNumber(attribute)
        : renderValues(attribute)}
    </ExpandableCard>
  );

  const heading = (
    <Stack sx={{ gap: 1 }}>
      <Typography level={Level.H3}>{data?.family.name}</Typography>
      {partsCount !== undefined && (
        <Typography level={Level.BODY_MD}>
          {t('labelPartFinderPartsAvailable', { count: partsCount })}
        </Typography>
      )}
    </Stack>
  );

  return (
    <>
      {!isDrawer && (
        <>
          <Breadcrumbs
            breadcrumbs={[
              {
                text: t('labelPartFinderAssets'),
                onClick: () => navigateWithReturn(partFinderCatalogPath()),
              },
              { text: data?.family.name ?? '' },
            ]}
          />
          <PageTitle
            title={t('titlePartFinderCatalog')}
            subtitle={t('subtitlePartFinderCatalog')}
            buttons={
              returnTo
                ? [
                    <Button
                      key="cancel"
                      text={t('buttonLabelCancel')}
                      onClick={cancel}
                      size={Size.MD}
                    />,
                  ]
                : undefined
            }
          />
        </>
      )}

      {showError && <Error statusCode={getErrorStatusCode(error)} />}

      {!showError && (
        <Stack
          sx={{
            gap: 1,
            mb: 2,
            // On the page, pinned to the top while the cards scroll under it, painted in the page's
            // background like the button at the bottom. The drawer keeps the heading in its own
            // header. Above the cards' contents, which Joy paints at z-index 1.
            ...(!isDrawer && {
              position: 'sticky',
              top: 0,
              zIndex: 2,
              pt: 1,
              pb: 1.5,
              backgroundColor: 'background.level1',
              // Chrome pins the block below the page content's top padding (16px), where the cards
              // would still scroll past: the same background covers that strip.
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                top: -16,
                height: 16,
                backgroundColor: 'background.level1',
              },
            }),
          }}
        >
          {headingContainer ? createPortal(heading, headingContainer) : heading}
          <AppliedFilters filters={appliedFilters} onResetAll={resetAll} />
        </Stack>
      )}

      {!showError && (
        <Stack sx={{ gap: 2 }}>
          {/* The family setup's groups: a card with the group's name around its attributes. */}
          {getAttributeUnits(data?.attributes ?? []).map((unit) =>
            'group' === unit.type ? (
              <ExpandableCard
                key={`group-${unit.attributes[0].id}`}
                title={unit.name}
                variant={Variant.OUTLINED}
                defaultExpanded
              >
                <Stack sx={{ gap: 1.5 }}>{unit.attributes.map(renderAttribute)}</Stack>
              </ExpandableCard>
            ) : (
              renderAttribute(unit.attribute)
            )
          )}

          {/* Pinned to the bottom of whatever scrolls (the page or the drawer), and painted in
              its background so the cards do not show around the button; above the cards'
              contents, which Joy paints at z-index 1. */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 2,
              pt: 2,
              pb: 3,
              backgroundColor: isDrawer ? 'background.surface' : 'background.level1',
            }}
          >
            <Button
              text={
                pickedCount
                  ? t('buttonLabelShowPartNumbersCount', { count: data?.matchedCount ?? 0 })
                  : t('buttonLabelShowAllPartNumbers')
              }
              color={Color.PRIMARY}
              disabled={!partsCount}
              onClick={() => {
                onClose?.();
                navigateWithReturn(
                  `${partFinderFamilyPartsPath(familyId)}${selectionToSearch(selection)}`
                );
              }}
            />
          </Box>
        </Stack>
      )}
    </>
  );
};
